// components/EditOpportunityModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { X, Calendar, DollarSign, Search, Building2 } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditOpportunityModal = ({ opportunity, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [coaches, setCoaches] = useState([]);
  const [activeSection, setActiveSection] = useState("basic");

  // Account search state
  const [accountSearch, setAccountSearch] = useState("");
  const [accountResults, setAccountResults] = useState([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [selectedAccountName, setSelectedAccountName] = useState("");
  const accountSearchRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    // Basic Information
    name: "",
    accountId: "",
    type: "",
    telesalesPerson: "",
    assignedTo: "",
    coach: "",
    source: "",
    closeDate: "",
    salesStatus: "",
    lossReason: "",
    probability: "",
    referrer: "",

    // Address
    addressStreet: "",
    addressCity: "",
    addressStateCode: "",
    addressPostalCode: "",
    addressCountryCode: "United States",

    // Additional Fields
    upgradeDate: "",
    amount: "",
    followUp: false,
    followUpDate: "",

    // Requirements Section
    numberOfHandsets: "",
    monthlySpendWithCurrentProvider: "",
    currentPhoneSystemAge: "",
    unitsSold: "",
    contractTermLeft: "",
    contractEndDate: "",
    callerNotes: "",

    // Deal Score Section
    booked: false,
    bookedDate: "",
    twoMinChallenge: false,
    twoMinChallengeDate: "",
    nonQualSat: false,
    satNonQualDate: "",
    qualSat: false,
    qualSatDate: "",
    sold: false,
    soldDate: "",
    installComplete: false,
    installationDate: "",
    notSat: false,

    // Additional Information
    nextStep: "",
    leadSource: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCoaches();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(e.target) &&
        accountSearchRef.current &&
        !accountSearchRef.current.contains(e.target)
      ) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced account search
  useEffect(() => {
    if (!accountSearch.trim()) {
      setAccountResults([]);
      setShowAccountDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setAccountSearchLoading(true);
      try {
        const response = await apiService.getAccounts({
          search: accountSearch,
          limit: 10,
        });
        const accounts =
          response.data?.accounts || response.data || response.accounts || [];
        setAccountResults(accounts);
        setShowAccountDropdown(true);
      } catch (err) {
        console.error("Error searching accounts:", err);
      } finally {
        setAccountSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [accountSearch]);

  useEffect(() => {
    if (opportunity) {
      setSelectedAccountName(opportunity.account?.name || "");
      setFormData({
        name: opportunity.name || "",
        accountId: opportunity.accountId || "",
        type: opportunity.type || "",
        telesalesPerson: opportunity.telesalesPerson || "",
        assignedTo: opportunity.assignedTo || "",
        coach: opportunity.coachId || "",
        source: opportunity.source || "",
        closeDate: opportunity.expectedCloseDate
          ? new Date(opportunity.expectedCloseDate).toISOString().split("T")[0]
          : "",
        salesStatus: opportunity.salesStatus || "",
        lossReason: opportunity.lostReason || "",
        probability: opportunity.probability || "",
        referrer: opportunity.referrer || "",

        // Address
        addressStreet: opportunity.addressStreet || "",
        addressCity: opportunity.addressCity || "",
        addressStateCode: opportunity.addressStateCode || "",
        addressPostalCode: opportunity.addressPostalCode || "",
        addressCountryCode: opportunity.addressCountryCode || "United States",

        // Additional Fields
        upgradeDate: opportunity.upgradeDate
          ? new Date(opportunity.upgradeDate).toISOString().split("T")[0]
          : "",
        amount: opportunity.amount || "",
        followUp: opportunity.followUp || false,
        followUpDate: opportunity.followUpDate
          ? new Date(opportunity.followUpDate).toISOString().split("T")[0]
          : "",

        // Requirements Section
        numberOfHandsets: opportunity.numberOfHandsets || "",
        monthlySpendWithCurrentProvider:
          opportunity.monthlySpendWithCurrentProvider || "",
        currentPhoneSystemAge: opportunity.currentPhoneSystemAge || "",
        unitsSold: opportunity.unitsSold || "",
        contractTermLeft: opportunity.contractTermLeft || "",
        contractEndDate: opportunity.contractEndDate
          ? new Date(opportunity.contractEndDate).toISOString().split("T")[0]
          : "",
        callerNotes: opportunity.callerNotes || "",

        // Deal Score Section
        booked: opportunity.booked || false,
        bookedDate: opportunity.bookedDate
          ? new Date(opportunity.bookedDate).toISOString().split("T")[0]
          : "",
        twoMinChallenge: opportunity.twoMinChallenge || false,
        twoMinChallengeDate: opportunity.twoMinChallengeDate
          ? new Date(opportunity.twoMinChallengeDate)
              .toISOString()
              .split("T")[0]
          : "",
        nonQualSat: opportunity.nonQualSat || false,
        satNonQualDate: opportunity.satNonQualDate
          ? new Date(opportunity.satNonQualDate).toISOString().split("T")[0]
          : "",
        qualSat: opportunity.qualSat || false,
        qualSatDate: opportunity.qualSatDate
          ? new Date(opportunity.qualSatDate).toISOString().split("T")[0]
          : "",
        sold: opportunity.sold || false,
        soldDate: opportunity.soldDate
          ? new Date(opportunity.soldDate).toISOString().split("T")[0]
          : "",
        installComplete: opportunity.installComplete || false,
        installationDate: opportunity.installationDate
          ? new Date(opportunity.installationDate).toISOString().split("T")[0]
          : "",
        notSat: opportunity.notSat || false,

        // Additional Information
        nextStep: opportunity.nextStep || "",
        leadSource: opportunity.leadSource || "",
      });
    }
  }, [opportunity]);

  const fetchCoaches = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setCoaches(response.data || response.users || []);
    } catch (error) {
      console.error("Error fetching coaches:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        accountId: formData.accountId,
        type: formData.type || null,
        telesalesPerson: formData.telesalesPerson || null,
        assignedTo: formData.assignedTo || null,
        coachId: formData.coach || null,
        source: formData.source || null,
        expectedCloseDate: formData.closeDate || null,
        salesStatus: formData.salesStatus || null,
        lostReason: formData.lossReason || null,
        probability: formData.probability
          ? parseInt(formData.probability)
          : null,
        referrer: formData.referrer || null,
        amount: formData.amount ? parseFloat(formData.amount) : null,

        addressStreet: formData.addressStreet || null,
        addressCity: formData.addressCity || null,
        addressStateCode: formData.addressStateCode || null,
        addressPostalCode: formData.addressPostalCode || null,
        addressCountryCode: formData.addressCountryCode || null,

        upgradeDate: formData.upgradeDate || null,
        followUp: formData.followUp,
        followUpDate: formData.followUpDate || null,

        numberOfHandsets: formData.numberOfHandsets || null,
        monthlySpendWithCurrentProvider:
          formData.monthlySpendWithCurrentProvider
            ? parseFloat(formData.monthlySpendWithCurrentProvider)
            : null,
        currentPhoneSystemAge: formData.currentPhoneSystemAge
          ? parseFloat(formData.currentPhoneSystemAge)
          : null,
        unitsSold: formData.unitsSold ? parseFloat(formData.unitsSold) : null,
        contractTermLeft: formData.contractTermLeft
          ? parseFloat(formData.contractTermLeft)
          : null,
        contractEndDate: formData.contractEndDate || null,
        callerNotes: formData.callerNotes || null,

        booked: formData.booked,
        bookedDate: formData.bookedDate || null,
        twoMinChallenge: formData.twoMinChallenge,
        twoMinChallengeDate: formData.twoMinChallengeDate || null,
        nonQualSat: formData.nonQualSat,
        satNonQualDate: formData.satNonQualDate || null,
        qualSat: formData.qualSat,
        qualSatDate: formData.qualSatDate || null,
        sold: formData.sold,
        soldDate: formData.soldDate || null,
        installComplete: formData.installComplete,
        installationDate: formData.installationDate || null,
        notSat: formData.notSat,

        nextStep: formData.nextStep || null,
        leadSource: formData.leadSource || null,
      };

      await apiService.updateOpportunity(opportunity.id, submitData);
      onSuccess();
    } catch (error) {
      console.error("Error updating opportunity:", error);
      setError(error.response?.data?.message || "Failed to update opportunity");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sections = [
    { id: "basic", label: "Opportunity Information" },
    { id: "requirements", label: "Requirements" },
    { id: "dealScore", label: "Deal Score" },
    { id: "additional", label: "Additional Information" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-5xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between z-10 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isDarkMode ? "bg-green-600/10" : "bg-green-100"
              }`}
            >
              <DollarSign
                size={20}
                className={isDarkMode ? "text-green-400" : "text-green-600"}
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Edit Opportunity
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Update opportunity information
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Required Information Note */}
        <div className="px-6 pt-4">
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <span className="text-red-500">*</span> = Required Information
          </p>
        </div>

        {/* Section Tabs */}
        <div
          className={`border-b ${
            isDarkMode
              ? "border-gray-700 bg-[#13161B]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeSection === section.id
                    ? isDarkMode
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : "text-blue-600 border-b-2 border-blue-600"
                    : isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div
              className={`px-4 py-3 rounded-lg ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          {/* Opportunity Information */}
          {activeSection === "basic" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Opportunity Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="text-red-500">*</span> Opportunity Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="Enter opportunity name"
                    />
                  </div>

                  <div className="relative">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Account Name
                    </label>
                    {selectedAccountName && !showAccountDropdown && (
                      <div
                        className={`flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg ${
                          isDarkMode
                            ? "bg-blue-600/20 border border-blue-500/40"
                            : "bg-blue-100 border border-blue-200"
                        }`}
                      >
                        <Building2
                          size={14}
                          className={`shrink-0 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                        />
                        <span
                          className={`text-sm truncate flex-1 ${
                            isDarkMode ? "text-blue-300" : "text-blue-700"
                          }`}
                        >
                          {selectedAccountName}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAccountName("");
                            setFormData((prev) => ({ ...prev, accountId: "" }));
                            setAccountSearch("");
                          }}
                          className={`transition-colors ml-1 ${
                            isDarkMode
                              ? "text-gray-400 hover:text-white"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    <div className="relative" ref={accountSearchRef}>
                      <Search
                        size={15}
                        className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <input
                        type="text"
                        value={accountSearch}
                        onChange={(e) => setAccountSearch(e.target.value)}
                        onFocus={() =>
                          accountSearch && setShowAccountDropdown(true)
                        }
                        placeholder={
                          selectedAccountName
                            ? "Search to change account..."
                            : "Search accounts..."
                        }
                        className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                        }`}
                      />
                      {accountSearchLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {showAccountDropdown && accountResults.length > 0 && (
                      <div
                        ref={accountDropdownRef}
                        className={`absolute z-50 w-full mt-1 border rounded-lg shadow-xl max-h-48 overflow-y-auto ${
                          isDarkMode
                            ? "bg-gray-900 border-gray-700"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        {accountResults.map((account) => (
                          <button
                            key={account.id}
                            type="button"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                accountId: account.id,
                              }));
                              setSelectedAccountName(account.name);
                              setAccountSearch("");
                              setShowAccountDropdown(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                              isDarkMode
                                ? "hover:bg-gray-800"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <Building2
                              size={15}
                              className={`shrink-0 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            />
                            <div className="min-w-0">
                              <p
                                className={`text-sm truncate ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {account.name}
                              </p>
                              {account.addressCity && (
                                <p
                                  className={`text-xs truncate ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {account.addressCity}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showAccountDropdown &&
                      !accountSearchLoading &&
                      accountResults.length === 0 &&
                      accountSearch && (
                        <div
                          className={`absolute z-50 w-full mt-1 border rounded-lg shadow-xl px-4 py-3 text-sm ${
                            isDarkMode
                              ? "bg-gray-900 border-gray-700 text-gray-400"
                              : "bg-white border-gray-200 text-gray-500"
                          }`}
                        >
                          No accounts found for "{accountSearch}"
                        </div>
                      )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="text-red-500">*</span> Opportunity Owner
                    </label>
                    <select
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      {coaches.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Type
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      <option value="new_business">New Business</option>
                      <option value="existing_customer_upgrade">
                        Existing Customer - Upgrade
                      </option>
                      <option value="existing_customer_replacement">
                        Existing Customer - Replacement
                      </option>
                      <option value="existing_customer_downgrade">
                        Existing Customer - Downgrade
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Telesales Person
                    </label>
                    <select
                      name="telesalesPerson"
                      value={formData.telesalesPerson}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      {coaches.map((user) => (
                        <option key={user.id} value={user.name}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Referrer
                    </label>
                    <input
                      type="text"
                      name="referrer"
                      value={formData.referrer}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="Enter referrer"
                    />
                  </div>

                  {/* Address Section */}
                  <div className="space-y-2">
                    <h4
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Address
                    </h4>

                    <select
                      name="addressCountryCode"
                      value={formData.addressCountryCode}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                    </select>

                    <textarea
                      name="addressStreet"
                      value={formData.addressStreet}
                      onChange={handleChange}
                      placeholder="Address (Street)"
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                    />

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        name="addressCity"
                        value={formData.addressCity}
                        onChange={handleChange}
                        placeholder="Address (City)"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                        }`}
                      />
                      <input
                        type="text"
                        name="addressStateCode"
                        value={formData.addressStateCode}
                        onChange={handleChange}
                        placeholder="State/Province"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                        }`}
                      />
                    </div>

                    <input
                      type="text"
                      name="addressPostalCode"
                      value={formData.addressPostalCode}
                      onChange={handleChange}
                      placeholder="Address (ZIP/Postal Code)"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Upgrade Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="upgradeDate"
                        value={formData.upgradeDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Follow Up ?
                    </label>
                    <select
                      name="followUp"
                      value={formData.followUp}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Follow Up Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="followUpDate"
                        value={formData.followUpDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Coach
                    </label>
                    <select
                      name="coach"
                      value={formData.coach}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      {coaches.map((coach) => (
                        <option key={coach.id} value={coach.id}>
                          {coach.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Source
                    </label>
                    <select
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      <option value="web">Web</option>
                      <option value="phone">Phone Inquiry</option>
                      <option value="referral">Referral</option>
                      <option value="partner">Partner</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="text-red-500">*</span> Close Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="closeDate"
                        value={formData.closeDate}
                        onChange={handleChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="text-red-500">*</span> Sales Status
                    </label>
                    <select
                      name="salesStatus"
                      value={formData.salesStatus}
                      onChange={handleChange}
                      required
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      <option value="none">None</option>
                      <option value="appointment_booked">
                        Appointment Booked
                      </option>
                      <option value="not_sat">Not Sat</option>
                      <option value="sat_non_qual">Sat Non-Qual</option>
                      <option value="sat_qual">Sat Qual</option>
                      <option value="sold">Sold</option>
                      <option value="dead">Dead</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Loss Reason
                    </label>
                    <select
                      name="lossReason"
                      value={formData.lossReason}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    >
                      <option value="">--None--</option>
                      <option value="price">Price</option>
                      <option value="competitor">Lost to Competitor</option>
                      <option value="timing">Timing</option>
                      <option value="no_budget">No Budget</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Probability (%)
                    </label>
                    <input
                      type="number"
                      name="probability"
                      value={formData.probability}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Amount
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Requirements Section */}
          {activeSection === "requirements" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Requirements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Number of Handsets / Phone Extensions
                  </label>
                  <input
                    type="text"
                    name="numberOfHandsets"
                    value={formData.numberOfHandsets}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="Enter number"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Monthly spend with Current Provider
                  </label>
                  <input
                    type="number"
                    name="monthlySpendWithCurrentProvider"
                    value={formData.monthlySpendWithCurrentProvider}
                    onChange={handleChange}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Current Phone System Age (Years)
                  </label>
                  <input
                    type="number"
                    name="currentPhoneSystemAge"
                    value={formData.currentPhoneSystemAge}
                    onChange={handleChange}
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contract Term Left (Years)
                  </label>
                  <input
                    type="number"
                    name="contractTermLeft"
                    value={formData.contractTermLeft}
                    onChange={handleChange}
                    step="0.1"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Units Sold
                  </label>
                  <input
                    type="number"
                    name="unitsSold"
                    value={formData.unitsSold}
                    onChange={handleChange}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contract end date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="contractEndDate"
                      value={formData.contractEndDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                    />
                    <Calendar
                      size={16}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Caller Notes
                  </label>
                  <textarea
                    name="callerNotes"
                    value={formData.callerNotes}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="Enter caller notes..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Deal Score Section */}
          {activeSection === "dealScore" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Deal Score
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="booked"
                      checked={formData.booked}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">BOOKED</span>
                  </label>
                  {formData.booked && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Booked Date
                      </label>
                      <input
                        type="date"
                        name="bookedDate"
                        value={formData.bookedDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="twoMinChallenge"
                      checked={formData.twoMinChallenge}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">2 MIN CHALLENGE</span>
                  </label>
                  {formData.twoMinChallenge && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        2 MIN CHALLENGE Date
                      </label>
                      <input
                        type="date"
                        name="twoMinChallengeDate"
                        value={formData.twoMinChallengeDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="nonQualSat"
                      checked={formData.nonQualSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">NON QUAL SAT</span>
                  </label>
                  {formData.nonQualSat && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        SAT NON-QUAL Date
                      </label>
                      <input
                        type="date"
                        name="satNonQualDate"
                        value={formData.satNonQualDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="qualSat"
                      checked={formData.qualSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">QUAL SAT</span>
                  </label>
                  {formData.qualSat && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        QUAL SAT Date
                      </label>
                      <input
                        type="date"
                        name="qualSatDate"
                        value={formData.qualSatDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="sold"
                      checked={formData.sold}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">SOLD</span>
                  </label>
                  {formData.sold && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Sold Date
                      </label>
                      <input
                        type="date"
                        name="soldDate"
                        value={formData.soldDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 mb-2 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="installComplete"
                      checked={formData.installComplete}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">Install Complete</span>
                  </label>
                  {formData.installComplete && (
                    <div className="mt-2 relative">
                      <label
                        className={`block text-xs mb-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Installation Date
                      </label>
                      <input
                        type="date"
                        name="installationDate"
                        value={formData.installationDate}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                      <Calendar
                        size={16}
                        className={`absolute right-3 bottom-2 pointer-events-none ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                  }`}
                >
                  <label
                    className={`flex items-center gap-3 cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="notSat"
                      checked={formData.notSat}
                      onChange={handleChange}
                      className={`w-5 h-5 rounded focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-600 text-blue-600"
                          : "border-gray-300 text-blue-600"
                      }`}
                    />
                    <span className="font-medium">NOT SAT</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information Section */}
          {activeSection === "additional" && (
            <div className="space-y-4">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Additional Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Next Step
                  </label>
                  <input
                    type="text"
                    name="nextStep"
                    value={formData.nextStep}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                    }`}
                    placeholder="Enter next step"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Lead Source
                  </label>
                  <select
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="">--None--</option>
                    <option value="web">Web</option>
                    <option value="phone">Phone Inquiry</option>
                    <option value="partner">Partner Referral</option>
                    <option value="purchased_list">Purchased List</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            className={`flex items-center gap-3 pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {loading ? "Updating..." : "Update Opportunity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOpportunityModal;
